import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const articleImageUrls: Record<string, string> = {
  'a001': 'https://readdy.ai/api/search-image?query=Professional%20outdoor%20winter%20hiking%20scene%20with%20warm%20amber%20lighting%20and%20snowy%20mountain%20landscape%2C%20photographer%20style%2C%20soft%20golden%20hour%20light%20on%20snow-covered%20trail%2C%20modern%20outdoor%20lifestyle%20photography%2C%20warm%20tones%20with%20cold%20environment%20contrast%2C%20high%20detail%20composition&width=1200&height=600&seq=art-detail-a001&orientation=landscape',
  'a002': 'https://readdy.ai/api/search-image?query=Close-up%20of%20person%20correctly%20applying%20warming%20patch%20on%20clothing%20over%20lower%20back%2C%20lifestyle%20photography%2C%20warm%20cozy%20indoor%20setting%20with%20soft%20natural%20light%2C%20comfortable%20winter%20clothing%20detail%2C%20clean%20modern%20aesthetic%2C%20warm%20amber%20tones&width=1200&height=600&seq=art-detail-a002&orientation=landscape',
  'a003': 'https://readdy.ai/api/search-image?query=Outdoor%20construction%20worker%20in%20winter%20wearing%20heated%20gear%20with%20warm%20golden%20sunset%20lighting%2C%20professional%20documentary%20style%20photography%2C%20urban%20winter%20setting%20with%20soft%20snow%2C%20warm%20industrial%20aesthetic%2C%20authentic%20working%20scene%20composition&width=1200&height=600&seq=art-detail-a003&orientation=landscape',
  'a004': 'https://readdy.ai/api/search-image?query=High-tech%20heating%20product%20components%20with%20carbon%20fiber%20and%20modern%20materials%20on%20clean%20warm%20background%2C%20product%20technology%20photography%2C%20soft%20studio%20lighting%20with%20warm%20glow%2C%20innovation%20and%20technology%20aesthetic%2C%20professional%20commercial%20shot&width=1200&height=600&seq=art-detail-a004&orientation=landscape',
  'a005': 'https://readdy.ai/api/search-image?query=Winter%20travel%20essentials%20neatly%20arranged%20on%20warm%20wooden%20surface%2C%20lifestyle%20flat%20lay%20photography%2C%20soft%20natural%20window%20lighting%2C%20cozy%20winter%20aesthetic%20with%20warm%20amber%20tones%2C%20organized%20travel%20preparation%20scene%2C%20clean%20modern%20composition&width=1200&height=600&seq=art-detail-a005&orientation=landscape',
  'a006': 'https://readdy.ai/api/search-image?query=Elderly%20person%20comfortably%20reading%20in%20warm%20cozy%20living%20room%20with%20soft%20natural%20light%2C%20lifestyle%20photography%2C%20peaceful%20winter%20indoor%20scene%2C%20warm%20amber%20tones%20and%20comfortable%20atmosphere%2C%20elegant%20senior%20lifestyle%20composition&width=1200&height=600&seq=art-detail-a006&orientation=landscape',
};

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
              <i className="ri-file-search-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 mb-4">文章未找到</p>
            <button
              onClick={() => navigate('/articles')}
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              返回暖身指南
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const imgUrl = articleImageUrls[article.id] || '';

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `https://rejiujiu.com/articles/${article.id}/#article`,
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        articleSection: article.category,
        wordCount: article.content.length,
        publisher: { '@id': 'https://rejiujiu.com/#organization' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://rejiujiu.com' },
          { '@type': 'ListItem', position: 2, name: '暖身指南', item: 'https://rejiujiu.com/articles' },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://rejiujiu.com/articles/${article.id}` },
        ],
      },
    ],
  };

  const paragraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article className="py-10 md:py-14">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-[800px] mx-auto">
                <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
                  <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <Link to="/articles" className="hover:text-primary-600 transition-colors whitespace-nowrap">暖身指南</Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
                </nav>

                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime}阅读</span>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{article.date}</span>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 font-heading leading-tight">
                    {article.title}
                  </h1>
                  <p className="text-sm text-foreground-500">
                    作者：{article.author}
                  </p>
                </div>

                <div className="aspect-[2/1] rounded-xl overflow-hidden mb-8 bg-background-100">
                  <img
                    src={imgUrl}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="prose-custom">
                  {paragraphs.map((para, index) => {
                    if (para.startsWith('## ')) {
                      return (
                        <h2 key={index} className="text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-3 font-heading">
                          {para.replace('## ', '')}
                        </h2>
                      );
                    }
                    if (para.startsWith('### ')) {
                      return (
                        <h3 key={index} className="text-base md:text-lg font-semibold text-foreground-900 mt-6 mb-2 font-heading">
                          {para.replace('### ', '')}
                        </h3>
                      );
                    }
                    if (para.startsWith('**') && para.endsWith('**')) {
                      return (
                        <p key={index} className="text-sm text-foreground-700 mb-3 font-bold">
                          <strong>{para.replace(/\*\*/g, '')}</strong>
                        </p>
                      );
                    }
                    const lines = para.split('\n').filter(Boolean);
                    if (lines.length > 1 && lines.some((l) => /^\d+\./.test(l) || l.startsWith('- '))) {
                      const isOrdered = /^\d+\./.test(lines[0]);
                      const Tag = isOrdered ? 'ol' : 'ul';
                      return (
                        <Tag key={index} className={`text-sm text-foreground-700 mb-4 space-y-1.5 ${isOrdered ? 'list-decimal' : 'list-disc'} pl-5`}>
                          {lines.map((line, li) => (
                            <li key={li} className="leading-relaxed">
                              {line.replace(/^\d+\.\s*/, '').replace(/^-\s*/, '').replace(/\*\*/g, '')}
                            </li>
                          ))}
                        </Tag>
                      );
                    }
                    return (
                      <p key={index} className="text-sm text-foreground-700 mb-4 leading-relaxed">
                        {para.replace(/\*\*/g, '')}
                      </p>
                    );
                  })}
                </div>

                <div className="mt-12 pt-8 border-t border-background-200/70">
                  <Link
                    to="/articles"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回暖身指南
                  </Link>
                </div>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}